import { useEffect } from "react";
import { getLoggerRef } from "../integrations/logger/logger-bridge";

const WARMUP_AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true } as const;

/**
 * Resolves to false only when the browser has an explicit "denied" mic permission state.
 * Falls back to a getUserMedia probe (tracks stopped immediately) when the Permissions API
 * is unavailable or returns "prompt", since that state doesn't tell us the outcome.
 */
export async function checkMicPermission(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return true;
  if (navigator.permissions) {
    try {
      const perm = await navigator.permissions.query({ name: "microphone" as PermissionName });
      if (perm.state === "granted") return true;
      if (perm.state === "denied") return false;
      // state is 'prompt' — fall through to getUserMedia
    } catch {
      // Permissions API doesn't support microphone query — fall through to getUserMedia
    }
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}

/** Requests mic permission on mount so the browser prompt appears before recording starts, discarding the stream immediately. */
export function useMicPermissionWarmup(logContext: string) {
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    navigator.mediaDevices
      .getUserMedia({ audio: WARMUP_AUDIO_CONSTRAINTS })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
      })
      .catch((err) => {
        getLoggerRef()?.warn(`Mic permission not granted on ${logContext}`, { error: String(err) });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
